// EBPristjek/EBPristjek.tsx
import { useEffect, useRef, useState } from "react"
import { Row, Col, Button, Form, Spinner } from "react-bootstrap"
import ProductFetcher from "./product-fetcher"
import useFetch from "../../../_fetch"
import CsvFileList from "./csv-file-list"

type IGenerating = {
  feedGenerating: boolean
  googleUpdateRunning: boolean
  wpBrandUpdateRunning: boolean
  wpProductUpdateRunning: boolean
  isAddingCategory: boolean
}

export default function EBPristjek() {
  const { doFetch } = useFetch()
  const [csvFiles, setCsvFiles] = useState<string[]>([])
  const [productData, setProductData] = useState<string>("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isGeneratingFile, setIsGeneratingFile] = useState({ feedGenerating: false, googleUpdateRunning: false, wpBrandUpdateRunning: false, wpProductUpdateRunning: false, isAddingCategory: false })
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

const [newCategory, setNewCategory] = useState("")

const handleAddCategory = async () => {
  if (!newCategory.trim()) return
  setIsGeneratingFile(prev => ({ ...prev, isAddingCategory: true }))
  try {
    await doFetch("/eb-pristjek/add-category", { category: newCategory })

  } catch (err) {
    console.error("Fejl ved tilføj kategori:", err)
  }
}

  useEffect(() => {
    const fetchCsvFiles = async () => {
      const res = await doFetch("/eb-pristjek/get-feeds")
      const data:string[] = await res.json()
      const filesSorted = data.sort((a: string, b: string) => {
        const [aY, aM, aD, aH, aMin, aS] = a.split("_")[0].split('-')
        const [bY, bM, bD, bH, bMin, bS] = b.split("_")[0].split('-')
        const aDate = new Date(`${aY}-${aM}-${aD} ${aH}:${aMin}:${aS}`)
        const bDate = new Date(`${bY}-${bM}-${bD} ${bH}:${bMin}:${bS}`)

        return bDate.getTime() - aDate.getTime()
      })

      setCsvFiles(filesSorted)
    }
    fetchCsvFiles()
  }, [])

  useEffect(() => {
    const checkStatus = async () => {
      const res = await doFetch("/eb-pristjek/is-generating")
      const data = await res.json() as IGenerating
      setIsGeneratingFile(data)
    }
    checkStatus()
  }, [])

  useEffect(() => {
    if (!isGeneratingFile) return

    const interval = setInterval(async () => {
      try {
        const res = await doFetch("/eb-pristjek/is-generating")
        const data = await res.json() as IGenerating
        if (!data.feedGenerating && !data.googleUpdateRunning && !data.wpBrandUpdateRunning && !data.wpProductUpdateRunning && !data.isAddingCategory) {
          const csvResponse = await doFetch("/eb-pristjek/get-feeds")
          const files = await csvResponse.json()
          const filesSorted = files.sort((a: string, b: string) => {
            const [aY, aM, aD, aH, aMin, aS] = a.split("_")[0].split('-')
            const [bY, bM, bD, bH, bMin, bS] = b.split("_")[0].split('-')
            const aDate = new Date(`${aY}-${aM}-${aD} ${aH}:${aMin}:${aS}`)
            const bDate = new Date(`${bY}-${bM}-${bD} ${bH}:${bMin}:${bS}`)

            return bDate.getTime() - aDate.getTime()
          })
          setCsvFiles(filesSorted)
          setIsGeneratingFile(data)
          clearInterval(interval)
        }
      } catch (err) {
        console.error("Fejl ved check:", err)
      }
    }, 10000)

    return () => clearInterval(interval)
  }, [isGeneratingFile])

  const handleTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Tab") {
      e.preventDefault()
      const el = textareaRef.current
      if (!el) return

      const start = el.selectionStart
      const end = el.selectionEnd
      const newValue =
        productData.substring(0, start) + "\t" + productData.substring(end)
      setProductData(newValue)

      setTimeout(() => {
        el.selectionStart = el.selectionEnd = start + 1
      }, 0)
    }
  }

  const handleDeleteFile = async (fileName: string) => {
    try {
      await doFetch("/eb-pristjek/delete-feed", { file: fileName })
      setCsvFiles(prev => prev.filter(f => f !== fileName))
    } catch (err) {
      console.error("Kunne ikke slette fil:", err)
    }
  }

  const onDownload = async (fileName: string) => {
    try {
      const res = await doFetch("/eb-pristjek/download-feed/" + fileName)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = fileName
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error("Kunne ikke download fil:", err)
      alert(err)
    }
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)
    setIsGeneratingFile({ feedGenerating: true, googleUpdateRunning: false, wpBrandUpdateRunning: false, wpProductUpdateRunning: false, isAddingCategory: false })

    try {
      const lines = productData.split("\n")
      const productsRaw = lines.map(line => {
        const [name, ean] = line.split("\t")
        return { name, ean }
      })
      const products = productsRaw.filter(p => p.name && p.ean)
      await doFetch("/eb-pristjek/generate-product-feed", { products })
    } catch (err) {
      alert(`${err}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  const rowCount = productData
    .split("\n")
    .map(line => line.trim())
    .filter(line => line !== "").length

  const costUSD = (rowCount * 0.02).toFixed(2)
  const datafeedrRequests = Math.ceil(rowCount / 10)

  const handleUpdateGoogleProducts = async (fullUpdate: boolean) => {
    setIsGeneratingFile(prev => ({ ...prev, googleUpdateRunning: true }))
    try {
      await doFetch(`/eb-pristjek/update-google-products`, { fullUpdate })
      setIsGeneratingFile(prev => ({ ...prev, googleUpdateRunning: true }))
    } catch (err) {
      console.error("Fejl ved Google update:", err)
    }
  }

  const handleUpdateWpBrands = async () => {
    setIsGeneratingFile(prev => ({ ...prev, wpBrandUpdateRunning: true }))
    try {
      await doFetch("/eb-pristjek/update-wp-brands", {})
      setIsGeneratingFile(prev => ({ ...prev, wpBrandUpdateRunning: true }))
    } catch (err) {
      console.error("Fejl ved WP Brands update:", err)
    }
  }

  const handleUpdateWpProducts = async () => {
    setIsGeneratingFile(prev => ({ ...prev, wpProductUpdateRunning: true }))
    try {
      await doFetch("/eb-pristjek/update-wp-products", {})
      setIsGeneratingFile(prev => ({ ...prev, wpProductUpdateRunning: true }))
    } catch (err) {
      console.error("Fejl ved WP Products update:", err)
    }
  }

  const isBusy =
    isGeneratingFile.feedGenerating ||
    isGeneratingFile.googleUpdateRunning ||
    isGeneratingFile.wpBrandUpdateRunning ||
    isGeneratingFile.wpProductUpdateRunning ||
    isGeneratingFile.isAddingCategory ||
    isSubmitting

  return (
    <div>
      <h3>EB Pristjek</h3>
      <Row className="mt-4">
        <Col md={3}>
          <h5>Genererede CSV-filer</h5>
          <CsvFileList
            files={csvFiles}
            isGenerating={isGeneratingFile.feedGenerating}
            onDelete={handleDeleteFile}
            onDownload={onDownload}
          />

          <div className="mt-4 d-flex flex-column gap-2">
            <Button
              variant="info"
              disabled={isBusy}
              onClick={() => handleUpdateGoogleProducts(false)}
            >
              {isGeneratingFile.googleUpdateRunning ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Opdaterer Google
                </>
              ) : (
                "Opdater Google"
              )}
            </Button>
            <Button
              variant="info"
              disabled={isBusy}
              onClick={() => handleUpdateGoogleProducts(true)}
            >
              {isGeneratingFile.googleUpdateRunning ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Opdaterer Google
                </>
              ) : (
                "Opdater Google (fuld)"
              )}
            </Button>
            <Button
              variant="secondary"
              disabled={isBusy}
              onClick={handleUpdateWpBrands}
            >
              {isGeneratingFile.wpBrandUpdateRunning ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Opdaterer WP Brands...
                </>
              ) : (
                "Opdater WP Brands"
              )}
            </Button>
            <Button
              variant="secondary"
              disabled={isBusy}
              onClick={handleUpdateWpProducts}
            >
              {isGeneratingFile.wpProductUpdateRunning ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Opdaterer WP Products...
                </>
              ) : (
                "Opdater WP Products"
              )}
            </Button>
              <Form.Control
                type="text"
                placeholder="Indtast kategori"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
              />
              <Button
                variant="success"
                disabled={isBusy || !newCategory.trim()}
                onClick={handleAddCategory}
              >
                {isGeneratingFile.isAddingCategory ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Tilføjer kategori...
                  </>
                ) : (
                  "Tilføj kategori"
                )}
              </Button>
          </div>

        </Col>
        <Col md={9}>
          <ProductFetcher
            onProductsProcessed={(products) => {
              const formatted = products.map(p => `${p.name}\t${p.ean}`).join("\n")
              setProductData(formatted)
            }}
          />

          <Form.Group className="mb-3 mt-3" controlId="productTextarea">
            <Form.Label>Indsæt produktnavn og EAN</Form.Label>
            <Form.Control
              as="textarea"
              rows={10}
              ref={textareaRef}
              placeholder={"Produkt 1\t1234567890123\nprodukt2\t4324324567890"}
              value={productData}
              onChange={(e) => setProductData(e.target.value)}
              onKeyDown={handleTextareaKeyDown}
            />
          </Form.Group>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isGeneratingFile.feedGenerating || isGeneratingFile.googleUpdateRunning || isGeneratingFile.wpBrandUpdateRunning || isGeneratingFile.wpProductUpdateRunning || isSubmitting || !productData.trim()}
          >
            {isSubmitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Indsender...
              </>
            ) : (
              "Indsend"
            )}
          </Button>

          <div className="text-muted" style={{ lineHeight: "1.5" }}>
            <div>
              Dette opslag koster cirka <strong>${costUSD}</strong>. Se forbrug:{" "}
              <a
                href="https://platform.openai.com/usage"
                target="_blank"
                rel="noopener noreferrer"
              >
                platform.openai.com/usage
              </a>
            </div>
            <div>
              <strong>{rowCount > 0 ? datafeedrRequests : 0}</strong> API request
              {datafeedrRequests !== 1 ? "s" : ""} mod Datafeedr. Se antal tilbageværende tokens på:{" "}
              <a
                href="https://my.datafeedr.com/api"
                target="_blank"
                rel="noopener noreferrer"
              >
                my.datafeedr.com/api
              </a>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  )
}
